import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useMessaging } from "../context/MessagingContext";
import { RecipientListProvider } from "../context/RecipientListContext";
import DashboardLayout from "../components/common/DashboardLayout";
import ConversationList from "../components/messaging/ConversationList";
import ChatWindow from "../components/messaging/ChatWindow";
import { useMediaQuery } from "../hooks/useMediaQuery";

const Messages = () => {
  const { conversations } = useMessaging();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const lastUrlConversationId = useRef(null);
  const isNarrowLayout = useMediaQuery("(max-width: 768px)");

  const messagesContainerClassName = [
    "messages-container",
    isNarrowLayout && !selectedConversation && "messages-container--mobile-inbox",
    isNarrowLayout && selectedConversation && "messages-container--mobile-thread",
  ]
    .filter(Boolean)
    .join(" ");

  // Check if there's a conversation ID in URL params (only when URL actually changes)
  useEffect(() => {
    const conversationId = searchParams.get("conversation");
    const conversationIdNum = conversationId ? parseInt(conversationId) : null;
    
    // Only react to URL changes, not re-renders with the same URL
    if (conversationIdNum && lastUrlConversationId.current !== conversationIdNum) {
      lastUrlConversationId.current = conversationIdNum;
      
      // Find conversation in the list
      if (conversations.length > 0) {
        const conv = conversations.find(
          (c) => c.conversation_id === conversationIdNum
        );
        if (conv) {
          setSelectedConversation(conv);
        }
      }
    } else if (!conversationId && lastUrlConversationId.current !== null) {
      // URL param was removed
      lastUrlConversationId.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, conversations]);

  // Update selected conversation when conversations list updates (e.g., after fetching/creating)
  useEffect(() => {
    const conversationId = searchParams.get("conversation");
    const conversationIdNum = conversationId ? parseInt(conversationId) : null;

    // If URL param exists, prefer selecting that conversation whenever it appears
    if (conversationIdNum) {
      const conv = conversations.find(
        (c) => c.conversation_id === conversationIdNum
      );
      if (
        conv &&
        (!selectedConversation ||
          selectedConversation.conversation_id !== conversationIdNum)
      ) {
        setSelectedConversation(conv);
        return;
      }
    }

    // Otherwise, keep selected conversation in sync with refreshed data
    if (selectedConversation && conversations.length > 0) {
      const conv = conversations.find(
        (c) => c.conversation_id === selectedConversation.conversation_id
      );
      if (conv) {
        if (
          !selectedConversation.display_name ||
          selectedConversation.display_name !== conv.display_name
        ) {
          setSelectedConversation(conv);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, searchParams]);

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    // Clear URL param when manually selecting a conversation
    const currentParam = searchParams.get("conversation");
    if (currentParam && parseInt(currentParam) !== conversation.conversation_id) {
      setSearchParams({});
      lastUrlConversationId.current = null;
    }
  };

  const handleArchive = () => {
    setSelectedConversation(null);
    setSearchParams({});
    lastUrlConversationId.current = null;
  };

  const handleBackToInbox = () => {
    setSelectedConversation(null);
    setSearchParams({});
    lastUrlConversationId.current = null;
  };

  return (
    <DashboardLayout>
      <div className="messages-page-wrapper">
        <div className="messages-page">
          <RecipientListProvider>
            <div className={messagesContainerClassName}>
              <div className="messages-sidebar">
                <ConversationList
                  onSelectConversation={handleSelectConversation}
                  selectedConversationId={selectedConversation?.conversation_id}
                />
              </div>

              <div className="messages-main">
                <ChatWindow
                  conversation={selectedConversation}
                  onArchive={handleArchive}
                  showBackButton={isNarrowLayout && !!selectedConversation}
                  onBackToInbox={handleBackToInbox}
                />
              </div>
            </div>
          </RecipientListProvider>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Messages;
